/**
 * Toast de notification pour les nouvelles commandes
 * Avec regroupement anti-spam et actions rapides
 */

import { useState, useEffect, useRef } from 'react';
import { X, Eye, ShoppingCart, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import audioService from '../services/audioService';
import { getNotificationEventKey } from '../utils/commandeToastNotificationId';
import {
  isCommandeToastDismissed,
  markCommandeToastDismissed,
} from '../utils/commandeToastDismissSession';

const TOAST_DURATION = 5000; // 5 secondes
const GROUPING_WINDOW = 20000; // 20 secondes pour regrouper

const CommandeToast = ({ notification, onDismiss, onView }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(true);
  const [groupedCount, setGroupedCount] = useState(1);
  const timeoutRef = useRef(null);
  const groupingTimeoutRef = useRef(null);

  useEffect(() => {
    // Auto-dismiss après TOAST_DURATION
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setTimeout(() => {
        if (onDismiss) onDismiss();
      }, 300); // Attendre l'animation de fade-out
    }, TOAST_DURATION);

    // Fenêtre de regroupement : si plusieurs notifications arrivent rapidement
    groupingTimeoutRef.current = setTimeout(() => {
      // La fenêtre de regroupement est fermée, on ne peut plus regrouper
    }, GROUPING_WINDOW);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (groupingTimeoutRef.current) clearTimeout(groupingTimeoutRef.current);
    };
  }, [onDismiss]);

  const handleDismiss = () => {
    // Arrêter la répétition du son quand l'utilisateur ignore la notification
    audioService.stopRepeat();
    
    setIsVisible(false);
    setTimeout(() => {
      if (onDismiss) onDismiss();
    }, 300);
  };

  const handleView = () => {
    // Arrêter la répétition du son quand l'utilisateur interagit
    audioService.stopRepeat();
    
    if (onView) {
      onView(notification);
    } else {
      navigate('/commandes');
    }
    handleDismiss();
  };

  if (!notification) return null;

  const { type, data } = notification;
  const isRendezVous = type === 'rendezvous';

  return (
    <div
      className={`fixed top-4 right-4 z-50 max-w-sm w-full bg-white rounded-lg shadow-lg border-l-4 ${
        isRendezVous ? 'border-blue-500' : 'border-green-500'
      } transform transition-all duration-300 ${
        isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start flex-1">
            <div className={`flex-shrink-0 p-2 rounded-full ${
              isRendezVous ? 'bg-blue-100' : 'bg-green-100'
            }`}>
              {isRendezVous ? (
                <Calendar className="h-5 w-5 text-blue-600" />
              ) : (
                <ShoppingCart className="h-5 w-5 text-green-600" />
              )}
            </div>
            
            <div className="ml-3 flex-1">
              <p className="text-sm font-semibold text-gray-900">
                {isRendezVous ? 'Nouveau rendez-vous !' : 'Nouvelle commande !'}
              </p>
              
              {groupedCount > 1 ? (
                <p className="text-sm text-gray-600 mt-1">
                  +{groupedCount} {isRendezVous ? 'nouveaux rendez-vous' : 'nouvelles commandes'}
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mt-1">
                    {data?.numero || data?.id || 'Nouvelle commande'}
                  </p>
                  {data?.total && (
                    <p className="text-sm font-medium text-gray-900 mt-1">
                      {parseFloat(data.total).toFixed(2)}€
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <button
            onClick={handleDismiss}
            className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 flex space-x-2">
          <button
            onClick={handleView}
            className="flex-1 inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md transition-colors"
          >
            <Eye className="h-4 w-4 mr-2" />
            Voir
          </button>
          <button
            onClick={handleDismiss}
            className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Ignorer
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Gestionnaire de toasts avec regroupement anti-spam
 */
export const CommandeToastManager = ({ notifications = [], onDismiss, onView }) => {
  const [activeToasts, setActiveToasts] = useState([]);
  const groupingQueueRef = useRef([]);
  const processedEventKeysRef = useRef(new Set());

  useEffect(() => {
    if (!notifications || notifications.length === 0) {
      return undefined;
    }

    notifications.forEach((notification) => {
      const eventKey = getNotificationEventKey(notification);
      if (processedEventKeysRef.current.has(eventKey)) {
        return;
      }
      if (isCommandeToastDismissed(eventKey)) {
        processedEventKeysRef.current.add(eventKey);
        return;
      }
      processedEventKeysRef.current.add(eventKey);

      const now = Date.now();
      const recentToasts = groupingQueueRef.current.filter(
        (n) => now - n.timestamp < GROUPING_WINDOW,
      );

      if (recentToasts.length > 0) {
        setActiveToasts((prev) => {
          const updated = [...prev];
          const lastToast = updated[updated.length - 1];
          if (lastToast) {
            lastToast.groupedCount = (lastToast.groupedCount || 1) + 1;
            return updated;
          }
          return [...prev, { ...notification, eventKey, groupedCount: 1 }];
        });
      } else {
        setActiveToasts((prev) => [...prev, { ...notification, eventKey, groupedCount: 1 }]);
      }

      groupingQueueRef.current.push({ ...notification, eventKey, timestamp: now });
    });

    const cleanup = setInterval(() => {
      const now = Date.now();
      groupingQueueRef.current = groupingQueueRef.current.filter(
        (n) => now - n.timestamp < GROUPING_WINDOW * 2,
      );
    }, GROUPING_WINDOW);

    return () => clearInterval(cleanup);
  }, [notifications]);

  const handleDismiss = (eventKey) => {
    if (!eventKey) return;
    markCommandeToastDismissed(eventKey);
    processedEventKeysRef.current.add(eventKey);
    setActiveToasts((prev) => prev.filter((toast) => toast.eventKey !== eventKey));
    if (onDismiss) onDismiss(eventKey);
  };

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2">
      {activeToasts.map((toast) => (
        <CommandeToast
          key={toast.eventKey || `${toast.timestamp}-${toast.type}`}
          notification={toast}
          onDismiss={() => handleDismiss(toast.eventKey)}
          onView={onView}
        />
      ))}
    </div>
  );
};

export default CommandeToast;
